import { useState, useEffect, useRef, useCallback } from "react";
import { DataTable, type DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { ProgressBar } from "primereact/progressbar";
import { Link } from "react-router";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useApiPagination, useDebounce, useToast } from "sj-ui-toolkit/src/hooks";
import { useApiCache, generateCacheKey } from "sj-ui-toolkit/src/api/cache";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { downloadCsv } from "sj-ui-toolkit/src/utils/downloadFile";
import { ROWS_PER_PAGE_OPTIONS } from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

import { snakeCase } from "lodash";
import type {
  ProgressReportRow,
  ProgressReportResponse,
  ProgressReportFilters,
  ProgressReportFilterOptions,
} from "@/api/progressReport";
import {
  loadProgressReportPage,
  downloadProgressReportCsv,
  loadProgressFilterOptions,
  loadProgressCollectionsAsync,
} from "@/api/progressReport";
import { loadLevelsAsync } from "@/api/levels";
import type { CourseData } from "@/api/reports";
import {
  PROGRESS_STATUS_CONFIG,
  getProgressStatusKey,
} from "@/constants/assignmentsReport.constants";
import ProgressReportFiltersPanel from "@/components/reports/ProgressReportFilters";
import { withSkeleton, textBody, dateBody, statusBody } from "@/helpers/tableBody";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SORT_FIELD_MAP: Record<string, string> = {
  createdAt: "level_start_date",
  completedAt: "completion",
};

const toApiSortField = (field: string | null | undefined): string | null =>
  field ? (SORT_FIELD_MAP[field] ?? snakeCase(field)) : null;

const PROGRESS_REPORT_CACHE_TTL_MS = 5 * 60 * 1000;

const SIDEBAR_FILTER_KEYS: (keyof ProgressReportFilters)[] = [
  "status",
  "level",
  "category",
  "jobRole",
  "title",
  "manager",
  "managersEmail",
  "employeeNumber",
  "securityChampion",
  "department",
  "businessUnit",
  "team",
  "company",
  "country",
  "path",
  "unenrolled",
  "archived",
];

interface LevelsData {
  courses?: CourseData[];
  coursesByRank?: Record<string, CourseData>;
}

const buildApiParams = (
  f: ProgressReportFilters,
  sortField: string | null | undefined,
  sortOrder: number | null | undefined
) => ({
  sortable_column: toApiSortField(sortField),
  order_type: (sortOrder === 1 ? "asc" : "desc") as "asc" | "desc",
  query: f.global?.value ?? undefined,
  from: f.dateRange?.value?.[0]
    ? Math.floor(new Date(f.dateRange.value[0]).getTime() / 1000)
    : undefined,
  to: f.dateRange?.value?.[1]
    ? Math.floor(new Date(f.dateRange.value[1]).getTime() / 1000)
    : undefined,
  status: f.status?.value?.length ? f.status.value : undefined,
  level: f.level?.value?.length ? f.level.value : undefined,
  category: f.category?.value?.length ? f.category.value : undefined,
  job_role: f.jobRole?.value?.length ? f.jobRole.value : undefined,
  title: f.title?.value?.length ? f.title.value : undefined,
  department: f.department?.value?.length ? f.department.value : undefined,
  business_unit: f.businessUnit?.value?.length ? f.businessUnit.value : undefined,
  country: f.country?.value?.length ? f.country.value : undefined,
  company: f.company?.value?.length ? f.company.value : undefined,
  manager: f.manager?.value?.length ? f.manager.value : undefined,
  security_champion: f.securityChampion?.value?.length ? f.securityChampion.value : undefined,
  team: f.team?.value?.length ? f.team.value : undefined,
  managers_email: f.managersEmail?.value?.length ? f.managersEmail.value : undefined,
  employee_number: f.employeeNumber?.value?.length ? f.employeeNumber.value : undefined,
  path: f.path?.value?.length ? f.path.value : undefined,
  unenrolled: f.unenrolled?.value?.length ? f.unenrolled.value : undefined,
  archived: f.archived?.value?.length ? f.archived.value : undefined,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProgressReport() {
  useBreadcrumb({
    label: "Progress Reporting",
    route: AdminAppRoutes.routes.reporting.routes.progress.route,
    display: "none",
    resetTrail: true,
  });

  const hideTimeSpent = useHideTimeSpent();
  const { showToast } = useToast();
  const { getCache, setCache, clearCache } = useApiCache();

  // Keys we've cached for this report, so "Clear Filters" can reset just our
  // entries without wiping the shared ApiCache used elsewhere in the app.
  const cacheKeysRef = useRef<Set<string>>(new Set());

  // --- Pagination / data ---
  const { data, totalRecords, lazyState, onPage, onSort, onFilter } = useApiPagination({
    storageKey: "progressReport",
    dataKey: "roleEnrollments",
    calculateTotalRecords: (res: ProgressReportResponse, pageSize: number) => {
      if (!res?.roleEnrollments) return 0;
      const total = res.totalPages ?? 0;
      if (total <= 1) return res.roleEnrollments.length;
      return (total - 1) * pageSize + res.roleEnrollments.length;
    },
    loadData: async ({ page, perPage, sortField, sortOrder, filters }) => {
      const f = (filters ?? {}) as ProgressReportFilters;
      const params = {
        page,
        per_page: perPage,
        ...buildApiParams(f, sortField, sortOrder),
      };

      const cacheKey = generateCacheKey("loadProgressReportPage", params);
      cacheKeysRef.current.add(cacheKey);
      const cached = getCache<ProgressReportResponse>(cacheKey);
      if (cached?.data && Date.now() - cached.timestamp < PROGRESS_REPORT_CACHE_TTL_MS) {
        return cached.data;
      }

      const response = await loadProgressReportPage(params);
      setCache(cacheKey, response, false, null);
      return response;
    },
  });

  // Clears just this report's cached entries, without wiping the shared
  // ApiCache used elsewhere in the app.
  const clearReportCache = useCallback(() => {
    cacheKeysRef.current.forEach(key => clearCache(key));
    cacheKeysRef.current.clear();
  }, [clearCache]);

  // Reset this report's cache when navigating away so the next visit fetches
  // fresh data instead of serving stale entries from the shared ApiCache.
  useEffect(() => {
    return () => clearReportCache();
  }, [clearReportCache]);

  // --- Local UI state ---
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [filterOptions, setFilterOptions] = useState<ProgressReportFilterOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [levelsData, setLevelsData] = useState<LevelsData>({});
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [pathOptions, setPathOptions] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);

  const [searchText, setSearchText] = useState<string>(
    () => ((lazyState.filters as ProgressReportFilters)?.global?.value as string) ?? ""
  );
  const [dateRange, setDateRange] = useState<Date[] | null>(() => {
    const dr = (lazyState.filters as ProgressReportFilters)?.dateRange?.value;
    if (!dr?.[0]) return null;
    return [new Date(dr[0]), dr[1] ? new Date(dr[1]) : new Date(dr[0])];
  });

  const debouncedSearch = useDebounce(searchText, 300);

  // Stable ref so handlers always read latest filters without stale closures
  const filtersRef = useRef<ProgressReportFilters>(
    (lazyState.filters as ProgressReportFilters) ?? {}
  );
  useEffect(() => {
    filtersRef.current = (lazyState.filters as ProgressReportFilters) ?? {};
  });

  // Skip first render to avoid clearing cache on mount
  const searchMounted = useRef(false);
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    onFilter({
      ...lazyState,
      filters: { ...filtersRef.current, global: { value: debouncedSearch || null } },
    });
  }, [debouncedSearch]);

  // Date range: only fire when both dates set or fully cleared
  const handleDateRangeChange = (dates: Date[] | null) => {
    setDateRange(dates);
    const [from, to] = dates ?? [];
    const complete = Array.isArray(dates) && dates.length === 2 && from && to;
    onFilter({
      ...lazyState,
      filters: {
        ...filtersRef.current,
        dateRange: {
          value: complete ? [from.toISOString(), to.toISOString()] : null,
        },
      },
    });
  };

  // Load filter options once on mount
  useEffect(() => {
    loadProgressFilterOptions()
      .then(setFilterOptions)
      .catch((err: unknown) => console.error("Failed to load filter options:", err))
      .finally(() => setLoadingOptions(false));
  }, []);

  // Load levels once on mount
  useEffect(() => {
    loadLevelsAsync()
      .then(res =>
        setLevelsData({
          courses: (res?.courses ?? []).map(c => ({
            ...c,
            id: c.id == null ? undefined : String(c.id),
          })),
        })
      )
      .catch((err: unknown) => console.error("Failed to load levels:", err))
      .finally(() => setLoadingLevels(false));
  }, []);

  // Load tenant-scoped collections from svc-content for Category and Paths filters
  useEffect(() => {
    loadProgressCollectionsAsync()
      .then(({ courseCollections, questCollections }) => {
        setPathOptions(
          [...courseCollections, ...questCollections]
            .map(c => c.name)
            .filter((name): name is string => !!name)
        );
        setCategoryOptions([
          ...(courseCollections.length > 0 ? [{ label: "Course", value: "course" }] : []),
          ...(questCollections.length > 0 ? [{ label: "Quest", value: "quest" }] : []),
        ]);
      })
      .catch((err: unknown) => console.error("Failed to load collections:", err))
      .finally(() => setLoadingCollections(false));
  }, []);

  // --- Derived ---
  const activeSidebarFilterCount = SIDEBAR_FILTER_KEYS.reduce((count, key) => {
    const entry = (lazyState.filters as Record<string, { value: string[] } | undefined>)?.[key];
    return count + (entry?.value?.length ? 1 : 0);
  }, 0);

  // --- Handlers ---
  const handleFiltersApply = (updatedFilters: ProgressReportFilters) => {
    onFilter({ ...lazyState, filters: { ...filtersRef.current, ...updatedFilters } });
    setSidebarVisible(false);
  };

  const handleFiltersClear = () => {
    const { global, dateRange } = filtersRef.current;
    const retainedFilters: ProgressReportFilters = {};
    if (global !== undefined) retainedFilters.global = global;
    if (dateRange !== undefined) retainedFilters.dateRange = dateRange;
    onFilter({ ...lazyState, filters: retainedFilters });
    setSidebarVisible(false);
  };

  const handleClearAllFilters = () => {
    setSearchText("");
    setDateRange(null);
    onFilter({ ...lazyState, filters: {} });
    clearReportCache();
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const csv = await downloadProgressReportCsv(
        buildApiParams(filtersRef.current, lazyState.sortField, lazyState.sortOrder)
      );
      downloadCsv(csv, `progress-report-${new Date().toISOString().split("T")[0]}.csv`);
    } catch (error) {
      console.error("Failed to export progress report:", error);
      showToast({ severity: "error", detail: "Failed to export progress report" });
    } finally {
      setIsExporting(false);
    }
  };

  // --- Column bodies ---
  const emailBody = withSkeleton<ProgressReportRow>(row => {
    if (row.id) {
      return (
        <Link to={`${row.id}`} className={themeClasses.textPrimaryDarker}>
          {row.email}
        </Link>
      );
    }
    return <span>{row.email}</span>;
  }, "14rem");

  const firstNameBody = textBody<ProgressReportRow>("firstName");
  const lastNameBody = textBody<ProgressReportRow>("lastName");
  const pathNameBody = textBody<ProgressReportRow>("roleName");
  const levelNameBody = textBody<ProgressReportRow>("levelName");
  const timeSpentBody = withSkeleton<ProgressReportRow>(row => row.timeSpent ?? "00:00:00", "6rem");

  const progressStatusBody = statusBody<ProgressReportRow>(row => {
    const statusKey = getProgressStatusKey(row.status);
    const config = PROGRESS_STATUS_CONFIG[statusKey];
    const statusMap: Record<string, string> = {
      completed: "Completed",
      in_progress: "In Progress",
      not_started: "Not Started",
      paused: "Paused",
    };
    return {
      icon: config.icon || undefined,
      label: statusMap[statusKey] ?? row.status ?? "Not Started",
    };
  });

  const progressBody = withSkeleton<ProgressReportRow>(row => {
    const strProgress = row.requiredProgressPercent ?? "0%";
    const intProgress = Number.parseInt(String(strProgress).replace("%", ""), 10) || 0;
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <ProgressBar value={intProgress} showValue={false} style={{ height: "0.5rem" }} />
        </div>
        <span>{strProgress}</span>
      </div>
    );
  }, "8rem");

  const completedAtBody = dateBody<ProgressReportRow>("completedAt", "Not completed");
  const createdAtBody = dateBody<ProgressReportRow>("createdAt");
  const lastEngagementBody = dateBody<ProgressReportRow>("lastEngagement");
  const deletedAtBody = dateBody<ProgressReportRow>("deletedAt");
  const archivedBody = dateBody<ProgressReportRow>("archived");

  // --- Render ---
  return (
    <>
      <div className="mb-4">
        <h1>Progress Reporting</h1>
      </div>

      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex flex-wrap items-center gap-2">
          <IconField iconPosition="left" className="min-w-48">
            <InputIcon className="pi pi-search" />
            <InputText
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search progress records..."
              aria-label="Search progress records"
              data-testid="progress-search-input"
              className="w-full"
            />
          </IconField>

          <Calendar
            value={dateRange}
            onChange={e => handleDateRangeChange(e.value as Date[] | null)}
            selectionMode="range"
            dateFormat="mm/dd/yy"
            placeholder="Select date range"
            showButtonBar
            hideOnRangeSelection
            readOnlyInput
          />

          <Button
            icon="pi pi-filter-slash"
            label="Clear Filters"
            size="small"
            severity="secondary"
            onClick={handleClearAllFilters}
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="relative inline-flex">
            <Button
              icon="pi pi-filter"
              label="Advanced Filters"
              severity="secondary"
              outlined
              onClick={() => setSidebarVisible(true)}
            />
            {activeSidebarFilterCount > 0 && (
              <Badge value={activeSidebarFilterCount} className="absolute -top-2 -right-2" />
            )}
          </div>

          <Button
            icon="pi pi-download"
            label="Export data"
            loading={isExporting}
            disabled={isExporting}
            data-testid="progress-export-button"
            onClick={() => void handleExportCsv()}
          />
        </div>
      </div>

      {/* Table */}
      <DataTable
        value={data as unknown as ProgressReportRow[]}
        lazy
        paginator
        aria-label="Progress report"
        data-testid="progress-report-table"
        rows={lazyState.rows}
        first={lazyState.first}
        totalRecords={totalRecords}
        sortField={lazyState.sortField ?? undefined}
        sortOrder={lazyState.sortOrder as 1 | -1 | 0 | null | undefined}
        onPage={e => void onPage(e)}
        onSort={onSort}
        onFilter={onFilter}
        filters={lazyState.filters as DataTableFilterMeta}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        size="normal"
        emptyMessage="No progress records found."
        removableSort
        tableClassName="dt-responsive-text"
      >
        <Column field="email" header="Email" sortable style={{ width: "350px" }} body={emailBody} />
        <Column field="firstName" header="First Name" sortable body={firstNameBody} />
        <Column field="lastName" header="Last Name" sortable body={lastNameBody} />
        <Column field="roleName" header="Path Name" body={pathNameBody} />
        <Column
          field="status"
          header="Status"
          sortable
          style={{ minWidth: "9rem" }}
          body={progressStatusBody}
        />
        <Column field="progress" header="Progress" sortable body={progressBody} />
        {!hideTimeSpent && (
          <Column
            field="timeSpent"
            header="Time Spent"
            style={{ width: "150px" }}
            body={timeSpentBody}
          />
        )}
        <Column
          field="completedAt"
          header="Completion Date"
          sortable
          style={{ width: "150px" }}
          body={completedAtBody}
        />
        <Column field="levelName" header="Level Name" sortable body={levelNameBody} />
        <Column
          field="createdAt"
          header="Path Start Date"
          sortable
          style={{ width: "150px" }}
          body={createdAtBody}
        />
        <Column
          field="lastEngagement"
          header="Last Engagement"
          sortable
          style={{ width: "150px" }}
          body={lastEngagementBody}
        />
        <Column
          field="deletedAt"
          header="Path Deleted Date"
          style={{ width: "150px" }}
          body={deletedAtBody}
        />
        <Column field="archived" header="User Archived Date" sortable body={archivedBody} />
      </DataTable>

      <ProgressReportFiltersPanel
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        filterOptions={filterOptions}
        loadingOptions={loadingOptions}
        levelsData={levelsData}
        loadingLevels={loadingLevels}
        pathOptions={pathOptions}
        categoryOptions={categoryOptions}
        loadingCollections={loadingCollections}
        currentFilters={(lazyState.filters as ProgressReportFilters) ?? {}}
        onApply={handleFiltersApply}
        onClear={handleFiltersClear}
      />
    </>
  );
}
